import { useState, useRef } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { UploadCloud, Download, CheckCircle2, AlertCircle, ArrowRight } from "lucide-react";

export default function ConvertPO({ api, onConverted }) {
  const [dragActive, setDragActive] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [downloadName, setDownloadName] = useState(null);
  const [fileName, setFileName] = useState(null);
  const inputRef = useRef(null);

  const doConvert = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast.error("Please upload a .csv file");
      return;
    }
    setFileName(file.name);
    setProcessing(true);
    setResult(null);
    setDownloadUrl(null);

    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${api}/convert`, fd);
      const data = res.data;

      // Decode base64 xlsx to a Blob for download
      const binStr = atob(data.file_b64);
      const bytes = new Uint8Array(binStr.length);
      for (let i = 0; i < binStr.length; i++) bytes[i] = binStr.charCodeAt(i);
      const blob = new Blob([bytes], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = URL.createObjectURL(blob);
      const outName = data.filename || `converted_${Date.now()}.xlsx`;
      setDownloadUrl(url);
      setDownloadName(outName);
      setResult({
        matched: data.matched,
        unmatched: data.unmatched,
        matched_details: data.matched_details || [],
        unmatched_items: data.unmatched_items || [],
      });

      // Auto-trigger download
      const a = document.createElement("a");
      a.href = url;
      a.download = outName;
      document.body.appendChild(a);
      a.click();
      a.remove();

      toast.success(`Converted · ${data.matched} items matched`);
      if (onConverted) onConverted();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Conversion failed";
      toast.error(detail);
    } finally {
      setProcessing(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) doConvert(e.dataTransfer.files[0]);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      <Card className="lg:col-span-7 border-stone-200 shadow-sm rounded-xl">
        <CardHeader>
          <CardTitle className="font-heading text-2xl text-stone-900">Upload PO CSV</CardTitle>
          <CardDescription className="font-body text-stone-500">
            We&apos;ll grab item names from <span className="font-medium text-stone-900">Column G</span>{" "}
            and quantities from <span className="font-medium text-stone-900">Column K</span>, then
            drop the numbers into your template&apos;s Column C.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div
            data-testid="po-dropzone"
            onDragOver={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            className={`dropzone cursor-pointer border-2 border-dashed rounded-xl p-10 md:p-14 flex flex-col items-center justify-center text-center ${
              dragActive
                ? "drag-active border-amber-600 bg-amber-50/50"
                : "border-stone-300 bg-white hover:border-amber-500 hover:bg-amber-50/30"
            }`}
          >
            <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mb-4">
              <UploadCloud className="w-7 h-7 text-amber-700" />
            </div>
            <p className="font-heading font-semibold text-stone-900 text-lg">
              Drop your PO .csv file here
            </p>
            <p className="font-body text-sm text-stone-500 mt-1">or click to browse</p>
            {fileName && (
              <p
                data-testid="uploaded-file-name"
                className="font-body text-xs text-stone-600 mt-3 bg-stone-100 border border-stone-200 rounded-full px-3 py-1"
              >
                {fileName}
              </p>
            )}
            <input
              ref={inputRef}
              data-testid="po-file-input"
              type="file"
              accept=".csv"
              className="hidden"
              onChange={(e) => doConvert(e.target.files?.[0])}
            />
            <Button
              type="button"
              data-testid="po-convert-btn"
              disabled={processing}
              className="mt-6 rounded-full bg-amber-700 hover:bg-amber-800 text-white px-6 active:scale-95 transition-colors duration-200"
              onClick={(e) => {
                e.stopPropagation();
                inputRef.current?.click();
              }}
            >
              {processing ? "Converting..." : "Choose CSV file"}
            </Button>
          </div>

          {downloadUrl && (
            <div className="mt-6 flex items-center justify-between rounded-xl border border-emerald-200 bg-emerald-50 px-5 py-4">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-700" />
                <div>
                  <p
                    data-testid="download-ready-title"
                    className="font-heading font-semibold text-emerald-900"
                  >
                    Your file is ready
                  </p>
                  <p className="font-body text-xs text-emerald-800/80">{downloadName}</p>
                </div>
              </div>
              <a
                href={downloadUrl}
                download={downloadName}
                data-testid="download-link"
                className="inline-flex items-center gap-2 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2 font-body text-sm transition-colors duration-200"
              >
                <Download className="w-4 h-4" /> Download again
              </a>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="lg:col-span-5 border-stone-200 shadow-sm rounded-xl">
        <CardHeader>
          <CardTitle className="font-heading text-xl text-stone-900">Conversion summary</CardTitle>
          <CardDescription className="font-body text-stone-500">
            A quick look at what matched and what got skipped.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!result ? (
            <p className="font-body text-sm text-stone-500">
              Upload a CSV to see the item-by-item match here.
            </p>
          ) : (
            <div>
              <div className="grid grid-cols-2 gap-3 mb-5">
                <StatBox
                  testid="matched-count"
                  label="Matched"
                  value={result.matched}
                  tone="success"
                />
                <StatBox
                  testid="unmatched-count"
                  label="Skipped"
                  value={result.unmatched}
                  tone="muted"
                />
              </div>

              {result.matched_details?.length > 0 && (
                <div className="mb-5">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-700" />
                    <span className="font-heading font-semibold text-sm text-stone-900">
                      Matched items
                    </span>
                  </div>
                  <div className="max-h-56 overflow-y-auto rounded-lg border border-stone-200 divide-y divide-stone-100 bg-white">
                    {result.matched_details.map((m, i) => (
                      <div
                        key={i}
                        data-testid={`matched-row-${i}`}
                        className="px-3 py-2 text-xs font-body flex items-center gap-2"
                      >
                        <span className="text-stone-500 truncate flex-1" title={m.po_name}>
                          {m.po_name}
                        </span>
                        <ArrowRight className="w-3 h-3 text-stone-400 flex-shrink-0" />
                        <span className="text-stone-900 truncate flex-1 font-medium" title={m.matched_to}>
                          {m.matched_to}
                        </span>
                        <Badge className="bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-50">
                          {m.qty}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {result.unmatched_items?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="w-4 h-4 text-stone-500" />
                    <span className="font-heading font-semibold text-sm text-stone-900">
                      Skipped (no template match)
                    </span>
                  </div>
                  <div className="max-h-40 overflow-y-auto rounded-lg border border-stone-200 divide-y divide-stone-100 bg-white">
                    {result.unmatched_items.map((n, i) => (
                      <div
                        key={i}
                        data-testid={`unmatched-row-${i}`}
                        className="px-3 py-2 text-xs font-body text-stone-500 truncate"
                        title={n}
                      >
                        {n}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatBox({ label, value, tone, testid }) {
  const styles =
    tone === "success"
      ? "bg-emerald-50 border-emerald-200 text-emerald-800"
      : "bg-stone-50 border-stone-200 text-stone-700";
  return (
    <div className={`rounded-xl border p-4 ${styles}`} data-testid={testid}>
      <p className="font-body text-xs uppercase tracking-wider opacity-70">{label}</p>
      <p className="font-heading text-3xl font-bold mt-1">{value}</p>
    </div>
  );
}
